<template>
	<div class="calendar d-flex flex-column h-100 position-relative">
		<div style="height: 65px">
			<navigator @updateArrowDate="calculateSunTimes" :fullCalendar="fullCalendar"></navigator>
		</div>

		<div class="calendar-content-p d-flex flexGrow1 position-relative">
			<div class="flexGrow1">
				<div class="calendar-wrapper">
					<div id="calendar" @mousemove="positionCursorX"  ref="calendarWidth"></div>
				</div>
			</div>

			<events @changeFilters="filtersChanged"></events>
		</div>

		<div style="min-height: 1rem;"></div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import { eventHub } from "@/main"

import $ from "jquery"
import debounce from 'lodash/debounce'
import Navigator from "./Navigator"
import Events from "./Events"
import Filters from "./Filters"

export default {
	data() {
		return {
			fullCalendar: null,
			calendarObject: null,
			defaultView: "agendaDay",
			start: null,
			end: null,
			loading: true,
			loaded: false,
			cancelEventsRequestToken: null,
			choosedChannel: null,
			targetEventID: null,
			isCursorLeft: null,
			// Default to today's date
			selectedDate: new Date().toISOString().substring(0, 10),
			lastWindow: null,
			onUpdateCalendar: null,
		};
	},
	beforeDestroy() {
		eventHub.$off('updateCalendar', this.onUpdateCalendar);
	},
	beforeMount() {
		this.calculateSunTimes();
	},
	created() {
		let currentView = this.$route.params.currentView || null;
		let expandedDay = this.$route.params.expandedDay || null;
		let expandedDate = this.nowDate();

		if (expandedDay) {
			expandedDate = expandedDay;
		}

		if (currentView) {
			this.updateCurrentView(currentView);

			if (currentView === "month") {
				this.defaultView = "month";
			} else {
				this.defaultView =
					"agenda" + (currentView.charAt(0).toUpperCase() + currentView.slice(1));
			}
		} else {
			if (this.getCurrentAccountType) {
				const defaultViewOptions = {
					0: 'agendaDay',
					1: 'agendaWeek',
					2: 'month'
				}

				this.defaultView = defaultViewOptions[this.getCurrentAccountType.defaultCalendarView];
			} else {
				this.updateCurrentView(this.defaultView.split("agenda").pop().toLowerCase());
			}
		}

		// Setting the currently selected date of the calendar | If there's no date supplied via route param, we'll use today's date
		this.updateExpandedDate(expandedDate);

		this.onUpdateCalendar = () => {
			if (!this.fullCalendar) return;

			this.flushEvents();
			this.fullCalendar.refetchEvents();
		}
	},
	async mounted() {
		await this.createCalendar();
		
		this.calculateSunTimes();
		eventHub.$on('updateCalendar', this.onUpdateCalendar);
	},
	watch: {
		'$route.params.expandedDay': {
			immediate: true,
			handler(newDay) {
				if (newDay && this.fullCalendar) {
					this.updateExpandedDate(newDay)
					this.fullCalendar.gotoDate(newDay)
				}
			}
		},
		'getCompanySettings.calendarTimeStart'(val) {
			if (!this.calendarObject) return;

			this.calendarObject.fullCalendar('option','scrollTime', val || '08:00:00')
			$('#calendar .icon-sunrise, #calendar .icon-sunset, #calendar .event-sun-line').remove();
			this.calculateSunTimes();
			this.fullCalendar.refetchEvents();
		},
		'getCompanySettings.isToShowSunriseAndSunsetOnCalendar'(show) {
			if (!this.fullCalendar) return;

			this.calculateSunTimes();
			this.fullCalendar.refetchEvents();
		},
		'$route.params.eventID': {
			immediate: true,
			handler(newEventId) {
				if (this.$route.name === 'calendarExpandedWithEventId' && newEventId) {
					this.closeModal();
					this.simulateEventSelection(newEventId);
				}
			}
		},
	},
	computed: {
		...mapGetters([
			"getExpandedDay",
			"getCurrentView",
			"getFilters",
			"getEvents",
			"account",
			"getSelectedEvent",
			"getCompanySettings",
			"getStoredEvents",
			"getEventColors",
			"getCurrentAccountType",
			"getSalesChannels"
		]),
		salesChannel() {
			let output = this.getSalesChannels.filter((c) => c.isImmutable);
			output.unshift({ brandName: "None Selected" });
			
			return output;
		},
		isToShowStartEndTime() {
			return this.getCurrentAccountType.isCalendarToShowStartEndTime;
		},
		isToShowProductName() {
			return this.getCurrentAccountType.isCalendarToShowProductName;
		},
		isToShowEventResourcesName() {
			return this.getCurrentAccountType.isCalendarToShowEventResourcesName;
		},
		isToShowQuantityAvailable() {
			return this.getCurrentAccountType.isCalendarToShowQuantityAvailable;
		},
		isToShowOccupancy() {
			return this.getCurrentAccountType.isCalendarToShowOccupancy;
		},
	},
	methods: {
		...mapActions([
			"commitEvents",
			"updateCurrentView",
			"updateExpandedDate",
			"updateSidebar",
			"pushEvent",
			"updateSelectedEvent",
			"openModal",
			"refreshAccountData",
			"initCompany",
			"downloadEvents",
			"prepareEventDates",
			"downloadEventColors",
			"flushEvents",
			"downloadSingleEvent",
			"closeModal",
			"calculateSunTimes",
			'downloadGenderPricePerChannel',
		]),
		filtersChanged() {
			eventHub.$emit('updateCalendar');
		},
		simulateEventSelection(eventId) {
			const vm = this;
			const elementId = `refid-${eventId}`;
			let timeoutId;

			vm.targetEventID = eventId;
			const waitToast = vm.$toasted.info('Wait! Loading your event…', { duration: 10000 });

			vm.$nextTick(() => {
				const el = document.getElementById(elementId);
				
				if (el) {
					clearTimeout(timeoutId);
					el.click();
					waitToast.goAway(0);
					vm.$toasted.success('Event found and loaded!');
				} else {
					// Wait to search for event
					const onRendered = () => {
						const e2 = document.getElementById(elementId);

						if (e2) {
							clearTimeout(timeoutId);
							eventHub.$off('eventsRendered', onRendered);
							e2.click();
							waitToast.goAway(0);
							vm.$toasted.success('Event found and loaded!', { duration: 3000 });
						}
					};
					eventHub.$on('eventsRendered', onRendered);

					// Event not found
					timeoutId = setTimeout(() => {
						eventHub.$off('eventsRendered', onRendered);
						waitToast.goAway(0);
						vm.$toasted.error('Event not found! Please update your filters and try again', { duration: 3000 });
					}, 10000);
				}
			});
		},
		positionCursorX(e) {
			const posX = e.clientX;
			const halfCalendarWidth = this.$refs.calendarWidth.clientWidth / 2;
			this.isCursorLeft = posX < halfCalendarWidth;
		},
		openSettings() {
			this.openModal("calendarSettings");
		},
		createCalendar(events) {
			let vm = this;
			let calendar = $("#calendar");
			let defaultColor = "bg-color-1";
			let canceledClass = "eventCanceled";
			let disabledClass = "eventDisabled";

			this.loading = false;
			this.loaded = true;

			// For more information about these parameters refer to - https://fullcalendar.io/docs/
			calendar.fullCalendar({
				locale: "en",
				firstDay: 1,
				allDaySlot: false,
				navLinks: true,
				defaultView: vm.defaultView,
				selectHelper: true,
				slotEventOverlap: false,
				nowIndicator: true,
				minTime: "00:00:00",
				scrollTime: this.getCompanySettings.calendarTimeStart
					? this.getCompanySettings.calendarTimeStart
					: "08:00:00", // If initSettings havent run it defaults to 08:00:00, it happens by pressing f5 on calendar
				maxTime: "24:00:00",
				lazyFetching: false,
				height: "parent",
				defaultTimedEventDuration: "01:00:00",
				timeFormat: "H:mm",
				slotLabelFormat: "H:mm",
				header: false,
				selectable: false, // Disabled for now
				editable: false, // Disabled for now
			   	events(start, end, timezone, callback) {
					const startKey = moment(start).format('YYYY-MM-DD');
					const endKey   = moment(end).format('YYYY-MM-DD');

					if (!vm.lastWindow || vm.lastWindow.start !== startKey || vm.lastWindow.end !== endKey) {
						vm.flushEvents();
						vm.lastWindow = { start: startKey, end: endKey };
					}

					calendar.fullCalendar('removeEvents');

					vm.downloadEvents({
						supplierId: vm.account.supplierId,
						dateFrom: startKey,
						dateTo: endKey
					}).then(() => {
						let eventsToShow = vm.getStoredEvents;

						callback(eventsToShow);
					});
				},
				views: {
					month: {
						columnFormat: "ddd",
						slotEventOverlap: false,
    					eventOverlap: false,
					},
					agenda: {
						columnFormat: "dddd D/M",
						slotEventOverlap: false,
    					eventOverlap: false,
					},
					agendaWeek: {
						columnFormat: "ddd D",
						slotEventOverlap: false,
    					eventOverlap: false,
					},
					agendaDay: {
						columnFormat: "dddd D/M",
						slotEventOverlap: false,
    					eventOverlap: false,
					},
				},
  				eventOverlap: false,

				// Triggered when a new date-range is rendered, or when the view type switches.
				viewRender(view, element) {
					vm.updateCurrentView(view.name);

					// Sdds the gears / settings icon to the top right
					$(".fc-head").append(
						'<span class="config-gear"><i class="fas fa-cog ml-2 pointer dark fs-large"></i></span>'
					);

					// Open Calendar Settings
					$(".config-gear").click((e) => {
						e.stopPropagation();
						vm.openSettings();
					});

					vm.calculateSunTimes();
				},

				// The events panel should update whenever we pick a specific day
				navLinkDayClick(date, jsEvent) {
					// This disables the click header on week view
					return false;

					let formattedDate = vm.formatDate(date);
					let view = vm.getCurrentView;

					// Toggle selected calendar event
					$(".fc-event").removeClass("selected");

					// Animate side panels
					$(".events .list").removeClass("moved-left");
					$(".events .more").addClass("moved-right");

					// Update route with new date
					vm.$router.push({
						name: "calendarExpanded",
						params: { currentView: view, expandedDay: formattedDate },
					});

					// Update sidebar with new date's events
					vm.updateSidebar(formattedDate);
				},

				// Select today's date by default
				eventAfterAllRender(view) {
					// Main function that maps all events and validates overlap
					const fixOverlappingEvents = () => {
						const container = document.getElementById('calendar');
						
						if (!container) return;

						const containerRect = container.getBoundingClientRect();

						const events = [...container.querySelectorAll('.fc-event:not(.event-sun-line)')];

						const widths = events.map(el => el.getBoundingClientRect().width);
						const minWidth = widths.length ? Math.min(...widths) : 0;
						const DEFAULT_BUCKET = 40;
  						const BUCKET_PX = Math.max(DEFAULT_BUCKET, minWidth);

						let rects = events.map(el => el.getBoundingClientRect());

						const MAX_PASSES = 6;
						const DELTA = 0.082;
						const MIN_OVERLAP_FRAC = 0.05;
						
						function overlap(rectA, rectB) {
							// Check the horizontal overlap
							const horizontalOverlap = !(rectA.right <= rectB.left || rectA.left >= rectB.right);
							if (!horizontalOverlap) return false;
							
							// Calculates the vertical intersection between the two rectangles
							const verticalOverlap = Math.min(rectA.bottom, rectB.bottom) - Math.max(rectA.top, rectB.top);
							if (verticalOverlap <= 0) return false;
							
							// Defines the minimum vertical overlap percentage to consider events "on the same line"
							const minHeight = Math.min(rectA.height, rectB.height);
							const verticalOverlapFraction = verticalOverlap / minHeight;
							
							return verticalOverlapFraction > MIN_OVERLAP_FRAC;
						}

						for (let pass = 0; pass < MAX_PASSES; pass++) {
							// Creating buckets
							const buckets = new Map();

							rects.forEach((r, idx) => {
								const key = Math.floor(r.left / BUCKET_PX);

								if (!buckets.has(key)) buckets.set(key, []);

								buckets.get(key).push(idx);
							});

							let changed = false;
							const writes = [];

							// Goes through each bucket + neighbors
							buckets.forEach((idxs, key) => {
								const group = [
									...(buckets.get(key - 1) || []),
									...idxs,
									...(buckets.get(key + 1) || [])
								];

								for (let a = 0; a < group.length; a++) {
									const elA = group[a];
									const rectA = rects[elA];

									for (let b = a + 1; b < group.length; b++) {
										const elB = group[b];
										const rectB = rects[elB];
										
										// If there is no overlap it doesn't change anything
										if (!overlap(rectA, rectB)) continue;

										// Identifies who is on the left and who is on the right at the moment
										const leftIsA = rectA.left < rectB.left;
										const leftIdx = leftIsA ? elA : elB;
										const rightIdx = leftIsA ? elB : elA;
										const leftRect = rects[leftIdx];
										const rightRect = rects[rightIdx];

										const availLeft   = leftRect.left - containerRect.left;
										const availRight  = containerRect.right - rightRect.right;
										const leftNarrow  = leftRect.width < rightRect.width;

										// Decide which one to move based on available space
										if (availLeft >= availRight && leftNarrow) {
											const refPct = parseFloat(events[rightIdx].style.left) || 0;
											const newRightPercentVal = (100 - refPct) - DELTA;
											
											// Assigns the new percentage in bucket
											writes.push({ el: events[leftIdx], prop: 'right', val: newRightPercentVal + '%' });
										} else {
											const refPct = parseFloat(events[leftIdx].style.right) || 0;
											const newLeftPercentVal = (100 - refPct) + DELTA;
											
											// Assigns the new percentage in bucket
											writes.push({ el: events[rightIdx], prop: 'left', val: newLeftPercentVal + '%' });
										}

										changed = true;
									}
								}
							});

							if (!changed) break;

							// Apply all style changes at once, avoiding multiple individual reflows
							writes.forEach(w => { w.el.style[w.prop] = w.val; });

							// Update moved elements’ bounding‐box cache to keep overlap checks accurate
							writes.forEach(w => {
								const idx = events.indexOf(w.el);
								rects[idx] = w.el.getBoundingClientRect();
							});
						}
					};

					const addSpecialHoverMove = () => {
						const events = Array.from(document.querySelectorAll('.fc-event'));

						for (let i = 0; i < events.length; i++) {
							const el = events[i];
							let eventEl = el.getBoundingClientRect();

							if (eventEl.top > 800) {
								el.addEventListener('mouseover', function(e) {
									e.currentTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
								})
							}
						}
					}

					$(".fc-day-header[data-date=" + vm.getExpandedDay + "]").addClass("selected");

					const selectedView = vm.getCurrentView;

					if (selectedView !== 'month') {
						// Align overlaped events
						fixOverlappingEvents();
					}

					// Adjust the position of small events near the bottom border on hover, so that all event details become fully visible.
					addSpecialHoverMove();

					eventHub.$emit('eventsRendered')
				},

				eventRender(event, element) {
					if (event.id === vm.targetEventID) {
						element.addClass('selected');
					} else {
						element.removeClass('selected');
					}

					// Check if the original timetables are available
					if (event.originalStart && event.originalEnd) {
						// Finds the element that displays the time
						const timeEl = element.find('.fc-time');
						if (timeEl.length) {
							const originalStart = new Date(event.originalStart);
							const originalEnd = new Date(event.originalEnd);
							const formatHM = (d) => String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
							// Update the content with the original times
							timeEl.html(`${formatHM(originalStart)} - ${formatHM(originalEnd)}`);
						}
					}

					// Dont render this event if it is marked as to hide from calendar
					if (event.hideOnCalendar && event.hideOnCalendar == true) {
						element.removeClass(defaultColor);
						return false;
					}

					if (event.eventType === "sunrise" || event.eventType === "sunset") {
						element.removeClass(defaultColor);
						element.css("display", "none");
					}

					if (event.id !== undefined && event.product !== undefined) {
						let str = "";
						str +=
							event.availabilityType == 2 && event.shareType == 1
								? '<div class="sharedEventIcon"><img src="/static/images/event/shared_product_icon_black.svg"></div>'
								: "";
						let now = moment().add(moment().utcOffset(), "minutes");
						let availability = null;
						if (event.availabilityType == 1) {
							availability = "Unlimited";
						} else if (event.availabilityType == 2) {
							if (event.shareType == 1) {
								availability = event.quantityAvailable;
							} else {
								availability = event.quantityAvailable;
							}
						} else {
							availability = event.quantityAvailable;
						}

						// Creates Array of used resources names
						let resourceOnEvent = [];
						for (let resource of event.eventResources) {
							resourceOnEvent.push(resource.name);
						}

						// Channel Availability - green / red point 
						let channelAvailability = (event) => {
							const choosedChannel = vm.salesChannel.find(
								(c) =>
									c.id ==
									vm.getCurrentAccountType.selectedSalesChannelToShowInIndicatorID
							); 
							if (event != null && choosedChannel && choosedChannel.name) {
								if (event.cutOffStatus === 1) {
									// open - green point
									return (
										'<div class="channelAvailability"><span title="Sales Channel ( ' +
										choosedChannel.name +
										' ) is OPEN" data-v-721dce1e="" class="channelCircle tooltip mr-1 radius-circle bg-medium bg-color-3"></span></div>'
									);
								} else if (event.cutOffStatus == 2) {
									// closed - red point
									return (
										'<div class="channelAvailability"><span title="Sales Channel ( ' +
										choosedChannel.name +
										' ) is CLOSED" data-v-721dce1e="" class="channelCircle tooltip mr-1 radius-circle bg-medium bg-color-6"></span></div>'
									);
								}
							}
						};

						// Occupancy rate per event
						let occupancyRate = (event) => {
							const isUnlimited = !event.hasLotation;
							let occupancy = event.maximumLotation == 0 ? "-" : `${Math.round((event.quantityBooked * 100) / event.maximumLotation)}%`;
							if (vm.isToShowOccupancy) {
								if (isUnlimited)
									return '<div class="occupancy-rate" style="margin-bottom: -5px; font-size: 19px; color: #6c6b6b;"><span title="This event has unlimited capacity.">&#8734;</span></div>';
								return (
									`<div class="occupancy-rate"><span title="Occupancy rate for this event is ${occupancy}">${occupancy}</span></div>`
								);
							}
						};

						let strings = {
							availability: vm.isToShowQuantityAvailable
								? '<div class="d-block fc-title fc-content--available-seats">Available: ' +
								  availability
								: "",
							product: vm.isToShowProductName
								? '<div class="d-block fc-title fc-content--product-name"><b>' +
								  eventNameToShow(event) +
								  "</b></div>"
								: "",
							resource: vm.isToShowEventResourcesName
								? '<div class="d-block fc-title fc-content--resource">' +
								  resourceOnEvent.join(", ") +
								  "</div>"
								: "",
							transport: '<div class="d-block fc-title"></div>',
						};

						function eventNameToShow() {
							let outputName = null;

							if (event.nameAlias) {
								outputName = event.nameAlias;
							} else {
								outputName = event.product.name;
							}

							// If is canceled
							if (event.status == -1) {
								outputName += " (Canceled)";
							}

							return outputName;
						}

						// Build availability string with allotted spaces
						if (event.allotted !== undefined && event.allotted > 0) {
							strings.availability +=
								" <span>(" + event.allotted + " allotted)</span></div>";
						} else {
							strings.availability += "</div>";
						}

						// If event has no reseource removes the resource string
						if (resourceOnEvent.length < 1) {
							strings.resource = "";
						}

						// Append information as needed for each true checkbox
						for (let filter in vm.getFilters) {
							if (vm.getFilters[filter]) {
								str += strings[filter];
							}
						}

						// Add unique class based in event ID
						element.attr("id", "refid-" + event.id);

						if ((!event.end && moment(event.start).isBefore(now)) || moment(event.end).isBefore(now)) {
							element.addClass("past");
						}

						// If event status is -1 (Canceled)
						if (event.status == -1) {
							element.addClass(canceledClass);
						}

						// If event status is -2 (Disabled)
						if (event.status == -2) {
							element.addClass(disabledClass);
						}

						// If event has any booking
						if (event.quantityBooked > 0) {
							element.addClass("hasBookings");
						}

						// If event is Virtual Roster we add a decoration
						if (event.shareType === 2) {
							element.addClass("virtualRooster");
						}

						// Add the right color for each event (taken from its product).
						// When creating a brand new event we still don't have a color so we default to defaultColor
						if (event.product !== undefined && event.product.color !== undefined) {
							// Get event color from store
							let color = vm.getEventColors.filter(function (storedColor) {
								return event.product.color == storedColor.id;
							});
							
							if (color && color.length > 0) {
								element.css({
									"background-color": color[0].code,
									"border-color": color[0].codeSecundary,
									"border-width": "1px",
								});
							}
							element.addClass("radius-normal");
						} else {
							element.addClass(defaultColor);
						}

						// Append information to the event
						element
							.find(".fc-content")
							.append('<div class="fc-column-first"></div>')
							.append('<div class="fc-column-second"></div>');

						element
							.find(".fc-column-first")
							.append(
								vm.isToShowStartEndTime
									? element.find(".fc-time").show()
									: element.find(".fc-time").hide()
							)
							.append(str);

						element
							.find(".fc-column-second")
							.append(channelAvailability(event) ? channelAvailability(event) : "") // green / red dot
							.append(occupancyRate(event) ? occupancyRate(event) : "");

						const targetOccupancy = element.find(".occupancy-rate");
						
						// On elements where isHigher or isSmaler are true we display the occupancy rate just on hover event.
						// This happens because we don't have enough space for all information when event isn't expanded
						if (vm.isToShowOccupancy) {
							vm.getCurrentView == "agendaWeek" ? targetOccupancy.hide() : "";
						}
						
						$(document).ready(function () {
							const childElementsHeightsIsHigherThanElementHeight = () => {
								const elmHeight = element.outerHeight();
								const timeHeight =
									element.find(".fc-time").outerHeight() != undefined
										? element.find(".fc-time").outerHeight()
										: 0;
								const prodNameHeight =
									element.find(".fc-content--product-name").outerHeight() !=
									undefined
										? element.find(".fc-content--product-name").outerHeight()
										: 0;
								const resourceHeight =
									element.find(".fc-content--resource").outerHeight() != undefined
										? element.find(".fc-content--resource").outerHeight()
										: 0;
								const availableSeatsHeight =
									element.find(".fc-content--available-seats").outerHeight() !=
									undefined
										? element.find(".fc-content--available-seats").outerHeight()
										: 0;
								const occupancyRateHeight =
									element.find(".occupancy-rate").outerHeight() != undefined
										? element.find(".occupancy-rate").outerHeight()
										: 0;
								const channelHeight =
									element.find(".channelAvailability").outerHeight() != undefined
										? element.find(".channelAvailability").outerHeight()
										: 0;

								const sumOfChildHeights =
									prodNameHeight +
									resourceHeight +
									availableSeatsHeight +
									timeHeight +
									channelHeight +
									occupancyRateHeight +
									3.2; // +3.2 is to compensate some padding of the container element
								const isHigher = sumOfChildHeights > elmHeight;

								return isHigher;
							};
							const elementWidthIsSmallerThenPresetWidth = () => {
								const timeHeightIsHidden = element.find(".fc-time").is(":hidden");
								const prodNameHeight = element
									.find(".fc-content--product-name")
									.outerHeight();
								const resourceHeight = element
									.find(".fc-content--resource")
									.outerHeight();
								const availableSeatsHeight = element
									.find(".fc-content--available-seats")
									.outerHeight();
								const occupancyRateHeight = element
									.find(".occupancy-rate")
									.outerHeight();
								const channelHeight = element
									.find(".channelAvailability")
									.outerHeight();

								const elementWidth = element.outerWidth();
								const presetWidth = 91;
								let isSmaller = false;
								if (
									timeHeightIsHidden == true &&
									prodNameHeight == undefined &&
									resourceHeight == undefined &&
									occupancyRateHeight == undefined &&
									availableSeatsHeight == undefined &&
									channelHeight == undefined
								) {
									// If element not visible, no need to check, so false
									isSmaller = false;
								} else {
									isSmaller = elementWidth < presetWidth;
								}

								return isSmaller;
							};

							// On elements where isHigher or isSmaler are true we display the occupancy rate just on hover event
							(vm.getCurrentView == "agendaDay" &&
								childElementsHeightsIsHigherThanElementHeight()) ||
							(vm.getCurrentView == "agendaDay" &&
								elementWidthIsSmallerThenPresetWidth())
								? targetOccupancy.hide()
								: "";

							const targetElementToExpand = element.find(".fc-content").parent();
							
							// Add / Remove CSS Class to expand element on hover
							targetElementToExpand
								.mouseenter(() => {
									// Occupancy rate is show on hover start. On months the occupancy rate has space to be showed
									if (vm.getCurrentView == "agendaWeek") {
										targetOccupancy.show();
									}
									if (
										(vm.getCurrentView == "agendaDay" &&
											childElementsHeightsIsHigherThanElementHeight()) ||
										(vm.getCurrentView == "agendaDay" &&
											elementWidthIsSmallerThenPresetWidth())
									) {
										targetOccupancy.show();
									}

									// Expand Width *** position the expanded element based on the cursor position - left/right
									if (elementWidthIsSmallerThenPresetWidth()) {
										vm.isCursorLeft
											? targetElementToExpand.addClass("expand-width-right")
											: targetElementToExpand.addClass("expand-width-left");
									}

									// Expand Width | Expands if sum of elements inside is higher than the container element
									targetElementToExpand.addClass(
										childElementsHeightsIsHigherThanElementHeight()
											? "expand-on-hover"
											: ""
									); 
								})
								.mouseleave(() => {
									if (targetElementToExpand.is('[class*="expand-"]')) {
										// Occupancy rate on week and day view is hide on hover ends.
										if (
											vm.getCurrentView == "agendaDay" ||
											vm.getCurrentView == "agendaWeek"
										) {
										targetOccupancy.hide();
										}
										targetElementToExpand
											.removeClass("expand-on-hover")
											.removeClass("expand-width-left")
											.removeClass("expand-width-right");
									}
								});
						});
					}

					if ((vm.getCompanySettings && vm.getCompanySettings.isToShowSunriseAndSunsetOnCalendar === true) || (element && element.isHidden === false)) {
						if (vm.getCurrentView === "month") {
							if (event.eventType === "sunrise" || event.eventType === "sunset") {
								element.css("display", "none");
								return false;
							}
						} else if (event.eventType === "sunrise" || event.eventType === "sunset") {
							element.css("display", "block");
							const iconClass =
								event.eventType === "sunrise"
									? "icon-sunrise large tooltip"
									: "icon-sunset large tooltip";
							const i = document.createElement("i");
							i.className = iconClass;
							i.title = `${event.title}: ${event.start.format("HH:mm")}`;

							let parentTargetElement;
							let targetElement;
							const minutes = event.start.minutes();

							if (minutes >= 30) {
								targetElement = document.querySelector(
									`.fc-time-grid.fc-unselectable tbody tr[data-time="${event.start.format(
										"HH"
									)}:30:00"] td`
								);
							} else {
								targetElement = document.querySelector(
									`.fc-time-grid.fc-unselectable tbody tr[data-time="${event.start.format(
										"HH"
									)}:00:00"] td`
								);
							}
							if (!targetElement) return null;
							parentTargetElement = targetElement.parentElement;

							parentTargetElement.querySelectorAll("i").forEach((icon) => {
								icon.remove();
							});

							let prevSibling = parentTargetElement.previousElementSibling;
							let nextSibling = parentTargetElement.nextElementSibling;
							let hasNearbyIcon = false;

							// Check 4 elements up
							for (let i = 0; i < 4 && prevSibling; i++) {
								if (prevSibling.querySelectorAll("i").length > 0) {
									hasNearbyIcon = true;
									break;
								}
								prevSibling = prevSibling.previousElementSibling;
							}

							// Check 4 elements down
							for (let i = 0; i < 4 && nextSibling; i++) {
								if (nextSibling.querySelectorAll("i").length > 0) {
									hasNearbyIcon = true;
									break;
								}
								nextSibling = nextSibling.nextElementSibling;
							}

							if (!hasNearbyIcon && parentTargetElement.querySelectorAll("i").length < 1) {
								targetElement.append(i);
							}

							element.css("border-top", "1px solid orange");
							element.find(".fc-time").hide();
							element.find(".fc-content").hide();
							element.find(".fc-column-first").hide();
							element.find(".fc-column-second").hide();
							element.find(".bg-color-1").remove();

							try {
								if (element && element[0]) {
									const eventParent = element[0];
									eventParent.classList.add("event-sun-line", "m-0", "p-0");

									if (eventParent.style) {
										eventParent.style.height = "0px";
										eventParent.style.setProperty(
											"background-color",
											"rgba(255, 165, 0, 0)",
											"important"
										);
										eventParent.style.setProperty("width", "100%");
										eventParent.style.left = 0;

										const minutesInSlot = 30;
										const formattedMinutes =
											minutes > 30 ? minutes - 30 : minutes;
										let percentage =
											(formattedMinutes / minutesInSlot) * 100 - 35;
										if (minutes === 0) {
											percentage = 0;
										} else if (minutes === 30) {
											percentage = 0;
										}
										i.style.top = `${percentage}%`;
									}
								}
							} catch (error) {
								console.error(
									"Error applying custom sunrise/sunset styling",
									error
								);
							}
						}
						event.display = "block";
					}

					if (event.isHidden === true) {
						element.display = "none";
					}
				},

				// Update the event through the API if the timeframes have changed after drag-and-drop
				eventDrop(event, delta, revertFunc) {
					if (delta > 0) {
						vm.pushEvent(event);
					}
				},

				// Update the event through the API if the timeframes have changed after resizing
				eventResize(event, delta, revertFunc) {
					if (delta > 0) {
						vm.pushEvent(event);
					}
				},

				// Open the event's details in the side panel when clicking on it on the calendar
				eventClick(calEvent, jsEvent, view) {
					if (calEvent.hasOwnProperty('code') && (calEvent.eventType === 'sunrise' || calEvent.eventType === 'sunset')) {
						return;
					}

					vm.targetEventID = calEvent.id;
					// Update the selected event
					vm.updateSelectedEvent(calEvent.id);
					vm.downloadGenderPricePerChannel(calEvent.id);
					
					// Toggle selected calendar event
					$(".fc-event").removeClass("selected");
					$(this).addClass("selected");

					// Animate side panels
					$(".events").addClass("showingMore overflow-y-auto");
					$(".events .list").addClass("moved-left");
					$(".events .more").removeClass("moved-right");
				},

				// Trigger the 'add event' modal whenever we click on an empty spot of the calendar
				select(start, end) {
					vm.start = start;
					vm.end = end;
				},
			});

			// We need to set the right date AFTER the calendar has been loaded,
			// otherwise we wouldn't have any vuex data to use (async load).
			// After everything is rendered we'll push the correct date, stored
			// in expandedDay
			calendar.fullCalendar("gotoDate", vm.getExpandedDay); // Dispara o callback viewRender

			vm.fullCalendar = calendar.fullCalendar("getCalendar");
			vm.calendarObject = calendar;
		},
	},
	components: {
		Navigator,
		Events,
		Filters,
	},
};
</script>

<style lang="scss">
	@import "../../assets/scss/_variables.scss";

	.channelCircle {
		width: 8px;
		height: 8px;
		display: inline-block;
	}

	.calendar-wrapper {
		height: 100%;
		touch-action: pan-y;
	}

	.fc {
		.fc-now-indicator {
			border: 1px solid rgba($brand, 0.6);
		}

		.fc-time-grid .fc-slats td {
			height: 2rem;
		}

		.fc-time-grid .fc-slats table {
			margin-bottom: 0.3rem;
		}

		thead {
			border: 0;

			td {
				border: 0;
				font-size: $font-size-small;
				padding: 0.9rem 0;
			}
		}

		th {
			border: 0;

			&.fc-day-header {
				&.fc-today {
					> a {
						color: $lightest;
						background-color: $brand;
						border-radius: 10rem;
						padding: 0.4rem 0.75rem;
					}
				}
			}
		}

		td {
			border: 1px solid $light;

			&.fc-today,
			&.fc-past,
			&.fc-future,
			&.fc-day.fc-today {
				background: transparent;
			}
		}

		.fc-event {
			border-color: $medium;
			border-radius: 0;
			margin: 0 0.3%;
			padding: 1%;

			&.eventCanceled {
				border-color: $color-6;
				border-width: 2px;
				border-style: solid;

				background: repeating-linear-gradient(
					45deg,
					$color-6,
					$color-6 10px,
					lighten($color-6, 5%) 10px,
					lighten($color-6, 5%) 20px
				);
			}

			&.eventDisabled {
				background-color: $light !important;
				color: $dark !important;
			}

			&.selected {
				border: 1px solid $lightest;
				outline: 1px dashed $dark;
			}

			.sharedEventIcon {
				position: absolute;
				right: 0;
				top: 0;
			}
		}

		.fc-body > tr > td.fc-widget-content {
			border: 0;
			background-color: $lightest;
		}

		.fc-agendaWeek-view {
			width: 100%;
			background-color: white;
			border-bottom: 1px solid #c1c3cd;

			.fc-time-grid-event {
				.fc-content {
					.virtualRooster {
						background: rgba(0, 0, 0, 0.3);
						height: 12px;
						width: 12px;
						border-radius: 50%;
						position: absolute;
						right: 5px;
						top: 5px;
					}
				}
			}
		}

		.fc-axis.fc-time.fc-widget-content {
			position: relative;
		}

		.fc-view-container {
			display: flex;
			flex-direction: column;
		}

		.fc-time-grid .fc-slats td {
			text-transform: uppercase;
			font-size: $font-size-small;
		}

		.fc-agendaDay-view {
			thead td {
				padding: 0.5rem 0 0.75rem;
				background-color: white;
				border-bottom: 1px solid #c1c3cd;
			}

			th.fc-day-header {
				text-align: left;

				> span {
					color: $darker;
					background-color: transparent;
					border-radius: 0;
					padding: 0;
					font-size: $font-size-larger;
				}

				&.fc-today {
					> span {
						font-family: "Open Sans Semibold", Helvetica, Arial, sans-serif;
					}
				}
			}

			.fc-event {
				padding: 2px 5px;
			}

			.fc-time-grid-event .fc-time,
			.fc-time-grid-event .fc-title {
				font-size: 0.7rem;
				margin-bottom: -2px;
			}
		}

		.fc-month-view {
			background-color: white;
			border-bottom: 1px solid #c1c3cd;

			.fc-day-grid-event {
				margin: 1% 0;
				padding: 0.5% 1%;
			}
		}
	}

	// Calendar event card
	.fc-event {
		&.virtualRooster {
			opacity: 0.8;

			&::after {
				content: "";
				background: url("~@/assets/images/badges/badge_vr_dark.svg");
				background-size: cover;
				background-position: center;
				height: 16px;
				width: 16px;
				position: absolute;
				right: 5px;
				top: 5px;
			}

			&.hasBookings {
				opacity: 1;
			}
		}
	}

	// Fix calendar height
	#calendar {
		.fc-view-container {
			.fc-view {
				& > table {
					display: flex;
					flex-direction: column;
					flex-grow: 1;

					.fc-body {
						overflow: auto;
						flex-grow: 1;
					}
				}
			}
		}

		.fc-time-grid .fc-event-container:hover {
			z-index: 1000 !important;
		}
	}

	.fc-content {
		height: 100%;
		width: 100%;
		display: flex;
		position: relative;
		flex-direction: row;

		.channelAvailability {
			position: absolute;
			z-index: 100000;
			bottom: 4px;
			right: -3px;
		}

		.occupancy-rate {
			font-size: $font-size-small;
			position: absolute;
			z-index: 100000;
			bottom: 0;
			right: 13px;
		}

		.fc-column-second {
			display: flex;
			flex-direction: column;
			justify-content: end;
		}

		.fc-column-first {
			flex-direction: column;
			margin-bottom: 18px;
		}
	}

	.config-gear {
		position: absolute;
		right: 0px;
		top: 8px;
		cursor: pointer;
		color: #75859a;
	}

	.fc-widget-header {
		border-right-width: 0 !important;
	}

	.expand-on-hover {
		z-index: 100 !important;
		bottom: auto !important;
	}

	.expand-width-left {
		left: auto !important;
		z-index: 99 !important;
		min-width: 7.5rem;
	}

	.expand-width-right {
		right: auto !important;
		z-index: 99 !important;
		min-width: 7.5rem;
	}

	.fc-event.event-sun-line {
		border-top: 1px solid orange;
		border-left: 0; 
		border-right: 0;
		border-bottom: 0;
		left: 0 !important;
	}
</style>



// WEBPACK FOOTER //
// Calendar.vue?f81804ca